import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Languages, Download, Eye, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
];

const DocumentTranslationPanel = ({ item, user }) => {
  const [data, setData] = useState(null);
  const [busyLang, setBusyLang] = useState(null);
  const [textView, setTextView] = useState(null);
  const pollRef = useRef(null);

  const authHeaders = () => {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchList = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/items/${item.id}/translations`, { headers: authHeaders() });
      setData(resp.data);
      return resp.data;
    } catch {
      return null;
    }
  }, [item.id]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Poll while any translation is running
  useEffect(() => {
    const anyRunning = (data?.translations || []).some((t) => t.status === "running");
    if (!anyRunning) return undefined;
    pollRef.current = setInterval(async () => {
      const fresh = await fetchList();
      const stillRunning = (fresh?.translations || []).some((t) => t.status === "running");
      if (!stillRunning && fresh) {
        clearInterval(pollRef.current);
        const done = (fresh.translations || []).filter((t) => t.status === "completed");
        if (done.length) toast.success("Document translation ready!");
      }
    }, 6000);
    return () => clearInterval(pollRef.current);
  }, [data, fetchList]);

  const getState = (code) => (data?.translations || []).find((t) => t.language === code);

  const requestTranslation = async (code) => {
    setBusyLang(code);
    try {
      const resp = await axios.post(
        `${API}/items/${item.id}/translate-document`,
        { language: code },
        { headers: authHeaders() },
      );
      if (resp.data.status === "running") {
        toast.success("Translation started — this takes a few minutes for long documents.");
      }
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start translation");
    } finally {
      setBusyLang(null);
    }
  };

  const downloadPdf = async (code, label) => {
    setBusyLang(code);
    try {
      const resp = await axios.get(`${API}/items/${item.id}/translations/${code}/download`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(item.title || "document").slice(0, 60)} (${label} translation).pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Please log in to download" : "Download failed");
    } finally {
      setBusyLang(null);
    }
  };

  const viewText = async (code, label) => {
    setBusyLang(code);
    try {
      const resp = await axios.get(`${API}/items/${item.id}/translations/${code}/text`, { headers: authHeaders() });
      setTextView({ label, text: resp.data.text });
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Please log in to view" : "Could not load translation");
    } finally {
      setBusyLang(null);
    }
  };

  if (!item?.has_file) return null;
  const anyAvailable = (data?.translations || []).some((t) => t.status !== "failed");
  if (!data || (!data.can_request && !anyAvailable)) return null;

  return (
    <div className="mt-6 pt-5 border-t border-slate-200" data-testid="doc-translation-panel">
      <div className="flex items-center gap-2 mb-3">
        <Languages className="w-4 h-4 text-orange-500" />
        <h3 className="text-sm font-semibold text-slate-800">AI Document Translation</h3>
      </div>
      <div className="flex flex-wrap gap-3">
        {LANGS.map(({ code, label }) => {
          const st = getState(code);
          if (st?.status === "completed") {
            return (
              <div key={code} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                <span className="text-xs font-medium text-green-800">{label}</span>
                <Button size="xs" variant="outline" className="h-6 text-xs px-2"
                  disabled={busyLang === code}
                  onClick={() => downloadPdf(code, label)}
                  data-testid={`download-translation-${code}`}>
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
                <Button size="xs" variant="outline" className="h-6 text-xs px-2"
                  disabled={busyLang === code}
                  onClick={() => viewText(code, label)}
                  data-testid={`view-translation-${code}`}>
                  <Eye className="w-3 h-3 mr-1" /> Read
                </Button>
              </div>
            );
          }
          if (st?.status === "running") {
            const p = st.progress || {};
            return (
              <div key={code} className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2"
                data-testid={`translation-progress-${code}`}>
                <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
                <span className="text-xs text-blue-800">
                  Translating to {label}… {p.done || 0}/{p.total || "?"}
                </span>
              </div>
            );
          }
          if (data.can_request) {
            return (
              <Button key={code} size="sm" variant="outline" className="text-xs"
                disabled={busyLang === code}
                onClick={() => requestTranslation(code)}
                data-testid={`request-translation-${code}`}>
                <Languages className="w-3 h-3 mr-1" />
                {st?.status === "failed" ? `Retry ${label}` : `Translate to ${label}`}
              </Button>
            );
          }
          return null;
        })}
      </div>
      {!data.can_request && (
        <p className="text-[11px] text-slate-400 mt-2">
          New translations are generated by subscribers and admins; completed ones are available to all logged-in readers.
        </p>
      )}

      <Dialog open={!!textView} onOpenChange={(o) => !o && setTextView(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>AI Translation — {textView?.label}</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-slate-400">Machine-translated from the original document — for reference only.</p>
          <div className="overflow-y-auto flex-1 whitespace-pre-wrap text-sm text-slate-700 leading-relaxed pr-2"
            data-testid="translation-text-view">
            {textView?.text}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentTranslationPanel;
